import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  BuildingEntity,
  FloorEntity,
  ItemCircuitEntity,
  ItemTemplateEntity,
  LocationEntity,
  RoomEntity,
} from '@/types/api';

import { getItemQueryOptions } from '../api/get-item';

export interface InfrastructureItemData {
  id: string;
  name: string;
  itemNumber: string;
  infrastructureRelationshipConfigs?: any[];
  itemTemplate?: Omit<ItemTemplateEntity, 'orgId' | 'projectId'> | null;
  circuits?: Omit<ItemCircuitEntity, 'orgId' | 'projectId' | 'itemId'>[];
  building?: Omit<BuildingEntity, 'projectId' | 'orgId' | 'buildingId'> | null;
  floor?: Omit<FloorEntity, 'projectId' | 'orgId' | 'buildingId'> | null;
  room?: Omit<RoomEntity, 'projectId' | 'orgId' | 'buildingId'> | null;
  locationEntity?: LocationEntity | null;
}

interface UseInfrastructureItemMappingOptions {
  itemIds: string[];
  enabled?: boolean;
  fetchCircuits?: boolean; // For parent items, we need to fetch their circuits
}

/**
 * Hook to efficiently fetch and map infrastructure item data (both parent and child items)
 * Uses React Query to fetch items and cache results
 *
 * @param itemIds - Array of item IDs to fetch (can be child items or parent items)
 * @param enabled - Whether the queries should be enabled
 * @param fetchCircuits - Whether to include circuit information (needed for parent items)
 */
export const useInfrastructureItemMapping = ({
  itemIds,
  enabled = true,
  fetchCircuits = false,
}: UseInfrastructureItemMappingOptions) => {
  const { orgId, projectId } = useScopeIds('org', 'project');

  // Use useQueries to fetch all items in parallel
  // Include building, floor, locationEntity relationships for room display
  const itemQueries = useQueries({
    queries: itemIds.map((itemId) => ({
      ...getItemQueryOptions(orgId, projectId, itemId, ['building', 'floor', 'locationEntity']),
      enabled: enabled && !!orgId && !!projectId,
    })),
  });

  // Build map from query results
  const itemsMap = useMemo(() => {
    const map = new Map<string, InfrastructureItemData>();

    itemQueries.forEach((query, index) => {
      const itemId = itemIds[index];
      const item = query.data;

      if (item) {
        const itemData: InfrastructureItemData = {
          id: item.id,
          name: item.name,
          itemNumber: item.itemNumber || '',
          infrastructureRelationshipConfigs: item.infrastructureRelationshipConfigs ?? [],
          itemTemplate: item.itemTemplate,
          building: item.building,
          floor: item.floor,
          room: item.room,
          locationEntity: item.locationEntity,
        };

        // Include circuits if requested (for parent items)
        if (fetchCircuits && item.circuits) {
          itemData.circuits = item.circuits;
        }

        map.set(itemId, itemData);
      }
    });

    return map;
  }, [itemQueries, itemIds, fetchCircuits]);

  // Check if any queries are still loading
  const isLoading = useMemo(() => {
    if (!enabled || itemIds.length === 0) return false;
    return itemQueries.some((query) => query.isLoading);
  }, [itemQueries, enabled, itemIds.length]);

  // Helper function to get item by ID
  const getItem = (itemId: string): InfrastructureItemData | undefined => {
    return itemsMap.get(itemId);
  };

  return {
    itemsMap,
    isLoading,
    getItem,
  };
};
